import { useEffect } from 'react';
import { useAtomValue } from 'jotai';
import { authErrorAtom, lastFailingEndpointAtom } from '../atoms/authAtom';

interface ErrorHandlerProps {
  onError?: (error: string) => void;
}

export function ErrorHandler({ onError }: ErrorHandlerProps) {
  const authError = useAtomValue(authErrorAtom);
  const lastFailingEndpoint = useAtomValue(lastFailingEndpointAtom);

  useEffect(() => {
    const handleError = (event: ErrorEvent) => {
      console.error('[ErrorHandler] Global error caught:', {
        message: event.error?.message,
        filename: event.filename,
        lineno: event.lineno,
        colno: event.colno,
        stack: event.error?.stack,
      });
      onError?.(event.error?.message ?? 'Unknown error occurred');
    };

    const handleUnhandledRejection = (event: PromiseRejectionEvent) => {
      console.error('[ErrorHandler] Unhandled promise rejection:', {
        reason: event.reason,
        promise: event.promise,
      });
      onError?.(event.reason?.message ?? 'Promise rejection occurred');
    };

    window.addEventListener('error', handleError);
    window.addEventListener('unhandledrejection', handleUnhandledRejection);

    return () => {
      window.removeEventListener('error', handleError);
      window.removeEventListener('unhandledrejection', handleUnhandledRejection);
    };
  }, [onError]);

  useEffect(() => {
    if (authError) {
      console.error('[ErrorHandler] Authentication error:', authError);
      onError?.(authError);
    }
  }, [authError, onError]);

  useEffect(() => {
    if (lastFailingEndpoint) {
      console.error('[ErrorHandler] API endpoint failed:', lastFailingEndpoint);
    }
  }, [lastFailingEndpoint]);

  // This component doesn't render anything, it just handles errors
  return null;
}

export default ErrorHandler;
